import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';

const SafetyCobrancaCard = () => {
  const [diagnostico, setDiagnostico] = useState(null);
  const [resultadoFix, setResultadoFix] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState('');

  const rodarDiagnostico = async () => {
    setCarregando(true);
    setErro('');
    setResultadoFix(null);
    try {
      const r = await apiService.api.get('/admin/diagnostico-cobrancas');
      setDiagnostico(r.data);
    } catch (e) {
      setErro(e?.response?.data?.detail || e.message);
    } finally {
      setCarregando(false);
    }
  };

  const rodarCorrecao = async () => {
    if (!window.confirm(
      'Deseja criar as cobranças faltantes agora? A operação é segura (idempotente — pode rodar várias vezes sem duplicar).'
    )) return;
    setExecutando(true);
    setErro('');
    try {
      const r = await apiService.api.post('/admin/garantir-cobrancas-ativas');
      setResultadoFix(r.data);
      await rodarDiagnostico();
    } catch (e) {
      setErro(e?.response?.data?.detail || e.message);
    } finally {
      setExecutando(false);
    }
  };

  const t = diagnostico?.totais;
  const ok = t && t.locacoes_sem_pagamento_aberto === 0 && t.total_semanas_faltantes_historico === 0;
  const totalPendencias = t ? (t.locacoes_sem_pagamento_aberto + t.total_semanas_faltantes_historico) : 0;

  return (
    <Card data-testid="card-safety-cobranca">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Shield className="h-5 w-5 text-cyan-500" />
          <span>Verificar e Corrigir Cobranças Semanais</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Verifica se TODA locação ativa tem um pagamento (pendente/atrasado) visível
          no painel. Se houver locação sem cobrança aberta, cria a próxima imediatamente.
          Útil para garantir que o número de pagamentos visíveis bata com o número de motos ativas.
        </p>

        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="btn-rodar-diagnostico-cobranca"
            onClick={rodarDiagnostico}
            disabled={carregando}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${carregando ? 'animate-spin' : ''}`} />
            {carregando ? 'Verificando...' : 'Rodar Diagnóstico'}
          </Button>
          {diagnostico && t && totalPendencias > 0 && (
            <Button
              data-testid="btn-corrigir-cobrancas"
              onClick={rodarCorrecao}
              disabled={executando}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {executando
                ? 'Criando...'
                : `Corrigir ${totalPendencias} pendência(s)`}
            </Button>
          )}
        </div>

        {erro && (
          <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-200">
            <AlertTriangle className="inline h-4 w-4 mr-1" /> {erro}
          </div>
        )}

        {diagnostico && (
          <div className="space-y-3" data-testid="diagnostico-cobranca-resultado">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <div className="rounded-md border p-2 bg-gray-50 dark:bg-gray-900/40">
                <div className="text-xs text-gray-500">Motos ativas</div>
                <div className="text-xl font-bold" data-testid="totais-locacoes-ativas">{t.locacoes_ativas}</div>
              </div>
              <div className="rounded-md border p-2 bg-gray-50 dark:bg-gray-900/40">
                <div className="text-xs text-gray-500">Com cobrança aberta</div>
                <div className="text-xl font-bold text-emerald-600">{t.locacoes_com_pagamento_aberto}</div>
              </div>
              <div className={`rounded-md border p-2 ${t.locacoes_sem_pagamento_aberto ? 'bg-rose-50 dark:bg-rose-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                <div className="text-xs text-gray-500">Sem cobrança aberta</div>
                <div className={`text-xl font-bold ${t.locacoes_sem_pagamento_aberto ? 'text-rose-600' : 'text-emerald-600'}`}
                     data-testid="totais-sem-pagamento">{t.locacoes_sem_pagamento_aberto}</div>
              </div>
              <div className={`rounded-md border p-2 ${t.total_semanas_faltantes_historico ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-emerald-50 dark:bg-emerald-900/20'}`}>
                <div className="text-xs text-gray-500">Semanas puladas</div>
                <div className={`text-xl font-bold ${t.total_semanas_faltantes_historico ? 'text-amber-600' : 'text-emerald-600'}`}
                     data-testid="totais-semanas-puladas">{t.total_semanas_faltantes_historico || 0}</div>
              </div>
              <div className="rounded-md border p-2 bg-gray-50 dark:bg-gray-900/40">
                <div className="text-xs text-gray-500">Pendente+atrasado</div>
                <div className="text-xl font-bold">{t.pagamentos_pendentes_e_atrasados}</div>
              </div>
            </div>

            {ok ? (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-800 dark:text-emerald-200">
                <CheckCircle2 className="inline h-4 w-4 mr-1" />
                Tudo certo: todas as locações ativas têm cobrança aberta e histórico completo.
              </div>
            ) : (
              <div className="space-y-2">
                {t.locacoes_sem_pagamento_aberto > 0 && (
                  <details className="rounded-md border border-rose-200 bg-rose-50/40 dark:bg-rose-900/10 p-3">
                    <summary className="cursor-pointer text-rose-700 dark:text-rose-200 font-medium">
                      ⚠️ {t.locacoes_sem_pagamento_aberto} locação(ões) sem cobrança aberta — ver detalhes
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs">
                      {diagnostico.detalhes.filter(d => !d.tem_pagamento_aberto).map(d => (
                        <li key={d.locacao_id} className="border-b border-rose-200/40 pb-1">
                          <code className="text-[11px]">{d.locacao_id.slice(0, 8)}…</code>{' '}
                          <Badge variant="outline" className="text-[10px]">{d.status_locacao}</Badge>{' '}
                          <span className="text-gray-500">
                            próx. esperada: <strong>{d.proxima_geracao_esperada}</strong>
                          </span>
                          {!d.tem_pagamento_recorrente && (
                            <Badge className="bg-amber-600 text-[10px] ml-1">sem recorrente</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {t.total_semanas_faltantes_historico > 0 && (
                  <details className="rounded-md border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 p-3">
                    <summary className="cursor-pointer text-amber-700 dark:text-amber-200 font-medium">
                      🕳️ {t.total_semanas_faltantes_historico} semana(s) pulada(s) no histórico em{' '}
                      {t.locacoes_com_semanas_faltantes} locação(ões) — ver detalhes
                    </summary>
                    <ul className="mt-2 space-y-1 text-xs">
                      {diagnostico.detalhes.filter(d => d.semanas_faltantes?.length).map(d => (
                        <li key={d.locacao_id} className="border-b border-amber-200/40 pb-1">
                          <code className="text-[11px]">{d.locacao_id.slice(0, 8)}…</code>{' '}
                          <span className="text-gray-500">
                            semanas pulads:{' '}
                            <strong>
                              {d.semanas_faltantes.map(s => `${s.semana}(${s.vencimento})`).join(', ')}
                            </strong>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {resultadoFix && (
          <div
            className="rounded-md border border-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 p-3 text-sm text-cyan-800 dark:text-cyan-200"
            data-testid="resultado-correcao-cobranca"
          >
            <strong>{resultadoFix.pagamentos_criados}</strong> cobrança(s) futura(s) criada(s) ·{' '}
            <strong>{resultadoFix.historicos_recuperados || 0}</strong> semana(s) histórica(s) recuperada(s) ·{' '}
            <strong>{resultadoFix.locacoes_processadas}</strong> locações verificadas ·{' '}
            <strong>{resultadoFix.erros}</strong> erro(s).
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SafetyCobrancaCard;
